// To-dos: every reminder the owner has, open and done, in one place.
//
// Home's "Needs your attention" shows the newest few; this is where the rest
// live, and where a tick can be taken back. Reads the same notifications the
// bell does, and the same done-ticks (device copy + server) Home writes.
import React, { useEffect, useState } from 'react';
import { Check, CreditCard, Building2, Clock, FileText, RotateCcw } from 'lucide-react';
import { groupTodos, readDoneIds, writeDoneIds, saveTodoDone, fetchPaymentTodos, TodoNotification } from './todos';
import { DEMO_NOTIFICATIONS } from '../portals/demo/demoNotifications';

interface Props {
  apiUrl: string;
  investorId?: string;
  language: string;
  demoMode?: boolean;
}

const PAGE = 10;

const tone = (type: string) =>
  type === 'payment' ? { bg: '#fef2f2', fg: '#b91c1c' }
  : type === 'construction' ? { bg: '#fffbeb', fg: '#b45309' }
  : type === 'reminder' ? { bg: '#eff6ff', fg: '#1d4ed8' }
  : { bg: '#f5f3ff', fg: '#6d28d9' };

const TodosView: React.FC<Props> = ({ apiUrl, investorId, language, demoMode }) => {
  const de = language === 'de';
  const [items, setItems] = useState<TodoNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [doneIds, setDoneIds] = useState<string[]>(() => readDoneIds(investorId));
  const [view, setView] = useState<'open' | 'done'>('open');
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => { setDoneIds(readDoneIds(investorId)); }, [investorId]);

  useEffect(() => {
    if (demoMode) { setItems(DEMO_NOTIFICATIONS as any); setLoading(false); return; }
    if (!investorId) return;
    const authToken = localStorage.getItem('authToken');
    Promise.all([
      fetch(`${apiUrl}/notifications/${encodeURIComponent(investorId)}`, { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} })
        .then(r => (r.ok ? r.json() : [])).catch(() => []),
      fetchPaymentTodos(apiUrl, investorId).catch(() => []),
    ])
      .then(([data, pay]) => setItems([...pay, ...(Array.isArray(data) ? data : [])]))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [apiUrl, investorId, demoMode]);

  const isDone = (n: TodoNotification) => !!n.done || doneIds.includes(n.id);
  const open = groupTodos(items.filter(n => !isDone(n)));
  const done = groupTodos(items.filter(isDone));
  const list = view === 'open' ? open : done;

  const setDone = (ids: string[], value: boolean) => {
    const next = value ? [...doneIds, ...ids.filter(id => !doneIds.includes(id))] : doneIds.filter(id => !ids.includes(id));
    setDoneIds(next);
    writeDoneIds(investorId, next);
    setItems(prev => prev.map(n => (ids.includes(n.id) ? { ...n, done: value, read: value ? true : n.read } : n)));
    if (!demoMode) ids.forEach(id => saveTodoDone(apiUrl, id, value));
  };

  const fmtDate = (d: Date | string) => new Date(d).toLocaleDateString(de ? 'de-DE' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div style={{ padding: '16px', paddingBottom: '110px', maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#000', margin: '4px 0 14px' }}>{de ? 'Aufgaben' : 'To-dos'}</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['open', 'done'] as const).map(v => (
          <button key={v} type="button" onClick={() => { setView(v); setLimit(PAGE); }}
            style={{
              padding: '9px 16px', borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${view === v ? '#000' : '#e5e7eb'}`, background: view === v ? '#000' : '#fff', color: view === v ? '#fff' : '#111827',
            }}>
            {v === 'open' ? (de ? `Offen (${open.length})` : `Pending (${open.length})`) : (de ? `Erledigt (${done.length})` : `Done (${done.length})`)}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: '#6b7280' }}>{de ? 'Wird geladen…' : 'Loading…'}</p>}
      {!loading && list.length === 0 && (
        <p style={{ color: '#6b7280', textAlign: 'center', padding: 24 }}>
          {view === 'open' ? (de ? 'Nichts offen — alles erledigt.' : 'Nothing pending — all done.') : (de ? 'Noch nichts erledigt.' : 'Nothing ticked off yet.')}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.slice(0, limit).map(({ n, ids, key }) => {
          const c = tone(n.type);
          const finished = view === 'done';
          return (
            <div key={key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#fff', borderRadius: 14, padding: 14, border: '1px solid #e5e7eb', opacity: finished ? 0.75 : 1 }}>
              <button
                type="button"
                role="checkbox"
                aria-checked={finished}
                aria-label={finished ? (de ? 'Wieder öffnen' : 'Mark as not done') : (de ? 'Als erledigt markieren' : 'Mark as done')}
                onClick={() => setDone(ids, !finished)}
                style={{
                  width: 26, height: 26, minWidth: 26, marginTop: 6, borderRadius: 8, padding: 0, cursor: 'pointer',
                  border: `2px solid ${finished ? '#16a34a' : '#d1d5db'}`, background: finished ? '#16a34a' : '#fff',
                  color: finished ? '#fff' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Check size={16} strokeWidth={3} />
              </button>
              <div style={{ width: 38, height: 38, minWidth: 38, borderRadius: 11, background: c.bg, color: c.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {n.type === 'payment' ? <CreditCard size={18} /> : n.type === 'construction' ? <Building2 size={18} /> : n.type === 'reminder' ? <Clock size={18} /> : <FileText size={18} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', lineHeight: 1.35, textDecoration: finished ? 'line-through' : 'none' }}>
                  {de && n.titleDe ? n.titleDe : n.title}
                </div>
                <div style={{ fontSize: 14, color: '#6b7280', marginTop: 3, lineHeight: 1.45 }}>{de && n.messageDe ? n.messageDe : n.message}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {fmtDate(n.timestamp)}
                  {finished && <><span>·</span><RotateCcw size={12} /> {de ? 'Tippen zum Wiederöffnen' : 'Tap the tick to reopen'}</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {list.length > limit && (
        <button type="button" onClick={() => setLimit(l => l + PAGE)}
          style={{ display: 'block', margin: '14px auto 0', padding: '10px 18px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 600, cursor: 'pointer' }}>
          {de ? `Mehr anzeigen (${list.length - limit})` : `Show more (${list.length - limit})`}
        </button>
      )}
    </div>
  );
};

export default TodosView;
